import {
  isAuthorizationDocumentId as isDocumentId,
  isRoleScopePolicy,
  normalizeManagedTeamIds,
  normalizeTenantRolePermissions,
} from "./authorization"
import type { RoleScopePolicy } from "./types/authorization"
import {
  GRANULAR_PERMISSIONS,
  type Permission,
  expandPermissionDependencies,
} from "./types/permissions"

interface MigrationDocument {
  /** Firestore document ID, never a value read from document data. */
  id: string
  data: Record<string, unknown>
}

export interface RoleAccessMigrationSnapshot {
  projectId: string
  databaseId: string
  roles: MigrationDocument[]
  users: MigrationDocument[]
  /** IDs are the user IDs from users/{uid}/profile/role. */
  assignments: MigrationDocument[]
  teams: MigrationDocument[]
}

interface RoleBefore {
  permissions: unknown
  scopePolicy: unknown
  activityScope: unknown
  systemRole: unknown
}

interface AssignmentBefore {
  publicRole: unknown
  privateRole: unknown
  managedTeamIds: unknown
}

export interface RoleAccessMigrationReview {
  projectId: string
  databaseId: string
  roles: {
    id: string
    before: RoleBefore
    scopePolicy: RoleScopePolicy
    activityScope: "user" | "admin"
    permissions: string[]
  }[]
  assignments: {
    id: string
    before: AssignmentBefore
    roleId: string
    managedTeamIds: string[]
  }[]
}

export interface RoleAccessMigrationChange {
  path: string
  /** Only the authorization fields covered by this patch. Null means absent. */
  before: Record<string, unknown> | null
  after: Record<string, unknown>
}

export interface RoleAccessMigrationPlan {
  mode: "dry-run"
  projectId: string
  databaseId: string
  /** All records are resolved. This is not deployment or apply authorization. */
  ready: boolean
  blockers: { kind: string; id: string }[]
  changes: RoleAccessMigrationChange[]
  privilegeDiffs: {
    id: string
    added: string[]
    removed: string[]
    scopeBefore: unknown
    scopeAfter: RoleScopePolicy
    activityScopeBefore: unknown
    activityScopeAfter: "user" | "admin"
    assignedUserIds: string[]
  }[]
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value)

const canonicalJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`)
      .join(",")}}`
  }
  return JSON.stringify(value ?? null)
}

const equal = (left: unknown, right: unknown) =>
  canonicalJson(left) === canonicalJson(right)

const indexById = <T extends { id: string }>(
  records: T[],
  label: string,
): Map<string, T> => {
  if (!Array.isArray(records)) throw new Error(`Invalid ${label} list`)
  const indexed = new Map<string, T>()
  for (const record of records) {
    if (!isRecord(record) || !isDocumentId(record.id)) {
      throw new Error(`Invalid ${label} ID`)
    }
    if (indexed.has(record.id))
      throw new Error(`Duplicate ${label} ID: ${record.id}`)
    indexed.set(record.id, record)
  }
  return indexed
}

const targetPermissions = new Set(GRANULAR_PERMISSIONS)
const normalizeTargetRole = (permissions: unknown, scope: unknown) => {
  const normalized = normalizeTenantRolePermissions(permissions, scope)
  if (!normalized || !normalized.every((p) => targetPermissions.has(p)))
    return null
  return expandPermissionDependencies(normalized).sort()
}

/** Platform roles need their own migration and are never planned here. */
export const isPlatformRole = (role: {
  id: string
  data: Record<string, unknown>
}) =>
  role.data.systemRole === "super-admin" ||
  role.id === "super-admin" ||
  (Array.isArray(role.data.permissions) && role.data.permissions.includes("*"))

type ResolvedRole = {
  scopePolicy: RoleScopePolicy
  activityScope: "user" | "admin"
  permissions: Permission[]
}

type SnapshotIndex = Record<
  "roles" | "users" | "assignments" | "teams",
  Map<string, MigrationDocument>
>

interface PlanState {
  plan: RoleAccessMigrationPlan
  block: (kind: string, id: string) => void
  resolvedRoles: Map<string, ResolvedRole>
  resolvedAssignments: Map<string, string>
}

/** Indexes the snapshot after checking its target and every document's data. */
function indexSnapshot(
  snapshot: RoleAccessMigrationSnapshot,
  review: RoleAccessMigrationReview | undefined,
): SnapshotIndex {
  if (
    !isRecord(snapshot) ||
    !isDocumentId(snapshot.projectId) ||
    !isDocumentId(snapshot.databaseId)
  ) {
    throw new Error("Snapshot project and database are required")
  }
  if (
    review &&
    (review.projectId !== snapshot.projectId ||
      review.databaseId !== snapshot.databaseId)
  ) {
    throw new Error("Review does not match the snapshot project and database")
  }
  const index: SnapshotIndex = {
    roles: indexById(snapshot.roles, "roles"),
    users: indexById(snapshot.users, "users"),
    assignments: indexById(snapshot.assignments, "assignments"),
    teams: indexById(snapshot.teams, "teams"),
  }
  for (const records of Object.values(index)) {
    for (const record of records.values()) {
      if (!isRecord(record.data))
        throw new Error(`Invalid document data: ${record.id}`)
    }
  }
  return index
}

/** Resolves one role's target access and records its change and privilege diff. */
function planRole(
  role: MigrationDocument,
  decision: RoleAccessMigrationReview["roles"][number] | undefined,
  state: PlanState,
) {
  const { id, data } = role
  if (isPlatformRole(role)) {
    state.block("platform-role-requires-separate-migration", id)
    return
  }
  const before: RoleBefore = {
    permissions: data.permissions ?? null,
    scopePolicy: data.scopePolicy ?? null,
    activityScope: data.activityScope ?? null,
    systemRole: data.systemRole ?? null,
  }
  if (decision && !equal(before, decision.before)) {
    state.block("stale-role-review", id)
    return
  }
  const scopePolicy = decision ? decision.scopePolicy : data.scopePolicy
  const activityScope = decision ? decision.activityScope : data.activityScope
  const permissions = normalizeTargetRole(
    decision ? decision.permissions : data.permissions,
    scopePolicy,
  )
  if (
    !isRoleScopePolicy(scopePolicy) ||
    !permissions ||
    (activityScope !== "user" && activityScope !== "admin")
  ) {
    state.block(decision ? "invalid-role-review" : "role-review-required", id)
    return
  }
  const after: ResolvedRole = { scopePolicy, activityScope, permissions }
  state.resolvedRoles.set(id, after)
  const current = {
    scopePolicy: data.scopePolicy,
    activityScope: data.activityScope,
    permissions: data.permissions,
  }
  if (equal(current, after)) return
  state.plan.changes.push({
    path: `public/data/roles/${id}`,
    before: {
      scopePolicy: before.scopePolicy,
      activityScope: before.activityScope,
      permissions: before.permissions,
    },
    after,
  })
  const oldPermissions = Array.isArray(data.permissions)
    ? data.permissions.filter((p): p is string => typeof p === "string")
    : []
  state.plan.privilegeDiffs.push({
    id,
    added: permissions.filter((p) => !oldPermissions.includes(p)),
    removed: [
      ...new Set(
        oldPermissions.filter((p) => !permissions.includes(p as Permission)),
      ),
    ].sort(),
    scopeBefore: before.scopePolicy,
    scopeAfter: scopePolicy,
    activityScopeBefore: before.activityScope,
    activityScopeAfter: activityScope,
    assignedUserIds: [],
  })
}

/** Managed teams must exist, and only assigned-teams roles may have them. */
const validManagedTeams = (
  managedTeamIds: string[] | null,
  role: ResolvedRole,
  teams: Map<string, MigrationDocument>,
): managedTeamIds is string[] =>
  !!managedTeamIds &&
  managedTeamIds.every((teamId) => isDocumentId(teamId) && teams.has(teamId)) &&
  (role.scopePolicy === "assigned-teams"
    ? managedTeamIds.length > 0
    : managedTeamIds.length === 0)

/** Resolves one user's role assignment and records public and private changes. */
function planAssignment(
  user: MigrationDocument,
  assignment: MigrationDocument | undefined,
  decision: RoleAccessMigrationReview["assignments"][number] | undefined,
  teams: Map<string, MigrationDocument>,
  state: PlanState,
) {
  const { id, data } = user
  const before: AssignmentBefore = {
    publicRole: data.role ?? null,
    privateRole: assignment?.data.role ?? null,
    managedTeamIds: assignment?.data.managedTeamIds ?? null,
  }
  if (decision && !equal(before, decision.before)) {
    state.block("stale-assignment-review", id)
    return
  }
  if (!decision && before.publicRole !== before.privateRole) {
    state.block("role-mismatch", id)
    return
  }
  const roleId = decision ? decision.roleId : before.privateRole
  const role =
    typeof roleId === "string" ? state.resolvedRoles.get(roleId) : undefined
  if (!isDocumentId(roleId) || !role) {
    state.block("unresolved-role", id)
    return
  }
  const managedTeamIds = normalizeManagedTeamIds(
    decision ? decision.managedTeamIds : before.managedTeamIds,
  )
  if (!validManagedTeams(managedTeamIds, role, teams)) {
    state.block(
      decision ? "invalid-assignment-review" : "assignment-review-required",
      id,
    )
    return
  }
  state.resolvedAssignments.set(id, roleId)
  const after = { role: roleId, managedTeamIds: managedTeamIds.sort() }
  if (data.role !== roleId) {
    state.plan.changes.push({
      path: `public/data/users/${id}`,
      before: { role: before.publicRole },
      after: { role: roleId },
    })
  }
  const privateBefore = assignment
    ? { role: before.privateRole, managedTeamIds: before.managedTeamIds }
    : null
  if (!equal(privateBefore, after)) {
    state.plan.changes.push({
      path: `users/${id}/profile/role`,
      before: privateBefore,
      after,
    })
  }
}

/**
 * Plans authorization-only patches from an offline tenant snapshot.
 * Legacy permission breadth and role/scope intent always require an explicit
 * review. systemRole and normal team membership never supply authorization.
 */
export const planRoleAccessMigration = (
  snapshot: RoleAccessMigrationSnapshot,
  review?: RoleAccessMigrationReview,
): RoleAccessMigrationPlan => {
  const { roles, users, assignments, teams } = indexSnapshot(snapshot, review)
  const roleReviews = indexById(review?.roles ?? [], "role reviews")
  const assignmentReviews = indexById(
    review?.assignments ?? [],
    "assignment reviews",
  )
  const plan: RoleAccessMigrationPlan = {
    mode: "dry-run",
    projectId: snapshot.projectId,
    databaseId: snapshot.databaseId,
    ready: false,
    blockers: [],
    changes: [],
    privilegeDiffs: [],
  }
  const state: PlanState = {
    plan,
    block: (kind, id) => plan.blockers.push({ kind, id }),
    resolvedRoles: new Map(),
    resolvedAssignments: new Map(),
  }

  for (const id of roleReviews.keys()) {
    if (!roles.has(id)) state.block("unknown-role-review", id)
  }
  for (const id of assignmentReviews.keys()) {
    if (!users.has(id)) state.block("unknown-assignment-review", id)
  }
  for (const role of roles.values()) {
    planRole(role, roleReviews.get(role.id), state)
  }
  for (const user of users.values()) {
    planAssignment(
      user,
      assignments.get(user.id),
      assignmentReviews.get(user.id),
      teams,
      state,
    )
  }
  for (const id of assignments.keys()) {
    if (!users.has(id)) state.block("orphan-assignment", id)
  }
  for (const diff of plan.privilegeDiffs) {
    diff.assignedUserIds = [...users.keys()]
      .filter(
        (id) =>
          users.get(id)?.data.role === diff.id ||
          assignments.get(id)?.data.role === diff.id ||
          state.resolvedAssignments.get(id) === diff.id,
      )
      .sort()
  }
  plan.ready = plan.blockers.length === 0
  return plan
}
